// The Conversations panel looks like WhatsApp (owner, 2026-09-25, A2): day
// separators, times, ticks, a label on every message. Pure — ChatThread draws.
//
// Times are the admin's local clock (Morocco), like the phone shows them.
// Guard: ./threadFormat.test.ts

const pad = (n: number) => String(n).padStart(2, '0');

/** A calendar day in local time — the separator's key. */
export function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function dayLabel(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(d)) / 86_400_000);
  if (days === 0) return 'Aujourd’hui';
  if (days === 1) return 'Hier';
  if (days > 1 && days < 7) return d.toLocaleDateString('fr-FR', { weekday: 'long' });
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function groupByDay<T>(items: T[], at: (item: T) => string, now: Date = new Date()): Array<{ key: string; label: string; items: T[] }> {
  const out: Array<{ key: string; label: string; items: T[] }> = [];
  for (const item of items) {
    const iso = at(item);
    const key = dayKey(iso);
    const last = out[out.length - 1];
    if (last && last.key === key) last.items.push(item);
    else out.push({ key, label: dayLabel(iso, now), items: [item] });
  }
  return out;
}

export function timeHM(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : '';
}

export type Ticks = { glyph: string; tone: 'grey' | 'blue' | 'red'; label: string } | null;

/** WhatsApp's own ticks. Only our side has them. */
export function ticksFor(status: string | null | undefined): Ticks {
  switch (status) {
    case 'sent': return { glyph: '✓', tone: 'grey', label: 'Envoyé' };
    case 'delivered': return { glyph: '✓✓', tone: 'grey', label: 'Distribué' };
    case 'read': return { glyph: '✓✓', tone: 'blue', label: 'Lu' };
    case 'failed': return { glyph: '!', tone: 'red', label: 'Non envoyé' };
    // Shown the instant it is sent from the panel, until WhatsApp confirms (2026-10-07).
    case 'pending': return { glyph: '🕓', tone: 'grey', label: 'Envoi en cours…' };
    case 'unconfirmed': return { glyph: '!', tone: 'grey', label: 'Pas encore confirmé par WhatsApp' };
    default: return null;
  }
}

export function senderLabel(sender: 'client' | 'agent' | 'person' | 'auto' | undefined): string {
  if (sender === 'agent') return 'Agent IA';
  // The phone's own WhatsApp Business greeting / away message — never a person (2026-10-21).
  if (sender === 'auto') return 'Message auto';
  if (sender === 'person') return 'Humain';
  return 'Client';
}

/** How long ago the message in the box was sent.
 *
 *  ⚠️ « ✓ Envoyé » ALONE READS AS "just now" whatever the clock says. The badge
 *  that misled the owner on 2026-09-04 was sixteen minutes old and looked
 *  identical to one from two seconds ago. */
export function sentAgo(at: number, t: (s: string) => string): string {
  const mins = Math.floor((Date.now() - at) / 60_000);
  if (mins < 1) return `(${t("à l'instant")})`;
  if (mins < 60) return `(${t('il y a')} ${mins} min)`;
  return `(${t('il y a')} ${Math.floor(mins / 60)} h)`;
}

/** Deleted « for everyone » — the gateway confirmed it (migration 194). */
export function isDeletedForEveryone(m: { delete_state?: string | null; deleted_at?: string | null }): boolean {
  return m.delete_state === 'deleted' || Boolean(m.deleted_at);
}

/**
 * May the panel offer « Supprimer pour tout le monde » on this message?
 *
 * Ours only, and only before the deadline the WORKER computed (`delete_until`:
 * 48 h after it left, null when WhatsApp never gave it an id). Not while a
 * delete is on its way; again after one failed. The worker refuses the rest
 * anyway — this only keeps the button off what it would refuse.
 */
export function canOfferDelete(
  m: { direction?: string | null; sender?: string | null; delete_state?: string | null; deleted_at?: string | null; delete_until?: string | null },
  nowMs: number = Date.now(),
): boolean {
  if (m.direction !== 'out' || m.sender === 'client') return false;
  if (isDeletedForEveryone(m) || m.delete_state === 'pending') return false;
  const until = Date.parse(String(m.delete_until ?? ''));
  return Number.isFinite(until) && nowMs < until;
}

/** How the paperclip sends a file: a photo WhatsApp shows as one, a video, or
 *  a file. Shared by the Conversations composer and the Simulator. */
export function kindForFile(file: { type: string }): 'image' | 'video' | 'document' {
  if (/^image\/(jpeg|jpg|png|webp|gif)$/i.test(file.type)) return 'image';
  if (/^video\//i.test(file.type)) return 'video';
  return 'document';
}

// ─── WHAT YOU JUST SENT, SHOWN AT ONCE (owner, 2026-10-07) ─────────────────
// « When I send a message it should show up instantly. » A message sent from
// the panel appears in the thread the moment Send is pressed, with a clock,
// and is replaced by the real one as soon as WhatsApp's copy arrives. Not
// confirmed after two minutes → it says so instead of pretending.
export interface PendingSend { id: string; body: string; atMs: number; media?: boolean }
export const PENDING_CONFIRM_MS = 2 * 60 * 1000;
const normBody = (s: string | null | undefined) => String(s ?? '').replace(/\s+/g, ' ').trim();

/** Pending sends not yet matched by a real outgoing message. Pure. */
export function openPending<M extends { direction: string; body: string | null; created_at: string; has_media?: boolean }>(
  messages: M[], pending: PendingSend[],
): PendingSend[] {
  return pending.filter((p) => !messages.some((m) => m.direction === 'out'
    && Date.parse(m.created_at) >= p.atMs - 60_000
    && (p.media ? Boolean(m.has_media) : normBody(m.body) === normBody(p.body))));
}

/** The pending sends as thread messages (clock, then « not confirmed »). Pure. */
export function pendingAsMessages(pending: PendingSend[], nowMs: number): import('@/lib/api').WhatsAppMessage[] {
  return pending.map((p) => ({
    id: p.id, wa_message_id: p.id, body: p.body, direction: 'out', push_name: null,
    has_media: false, media_mimetype: null, media_filename: null, wa_timestamp: null,
    created_at: new Date(p.atMs).toISOString(), sender: 'person', kind: 'text',
    status: nowMs - p.atMs > PENDING_CONFIRM_MS ? 'unconfirmed' : 'pending', quotable_id: null,
  }));
}
